/**
 * Product Comparison Feature
 *
 * CompareStore   — sessionStorage-backed store (max 5 products).
 * CompareBarElement  — sticky bottom bar: count + "Compare now" + "Clear selection".
 * CompareModalElement — full-screen comparison table modal.
 *
 * These are registered as custom elements and rendered by sections/custom-compare.liquid.
 */

const STORAGE_KEY = 'tilray-compare-products';
const MAX_PRODUCTS = 5;

// ---------------------------------------------------------------------------
// CompareStore (global singleton)
// ---------------------------------------------------------------------------

const CompareStore = {
  /** @returns {object[]} */
  getAll() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  },

  /**
   * Add a product. Returns false (and fires alert) if already at max.
   * @param {object} item
   * @returns {boolean}
   */
  add(item) {
    const items = this.getAll();
    const existingIdx = items.findIndex((i) => String(i.id) === String(item.id));
    if (existingIdx !== -1) {
      // Already present — update with latest data.
      items[existingIdx] = item;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      this._dispatch();
      return true;
    }
    if (items.length >= MAX_PRODUCTS) {
      window.alert(
        window.compareTranslations?.max_reached ||
          `You can compare up to ${MAX_PRODUCTS} products at a time.`
      );
      return false;
    }
    items.push(item);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    this._dispatch();
    return true;
  },

  /**
   * Remove a product by id.
   * @param {string|number} id
   */
  remove(id) {
    const items = this.getAll().filter((i) => String(i.id) !== String(id));
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    this._dispatch();
  },

  /** Clear all compared products. */
  clear() {
    sessionStorage.removeItem(STORAGE_KEY);
    this._dispatch();
  },

  /**
   * Check if a product is in the store.
   * @param {string|number} id
   * @returns {boolean}
   */
  has(id) {
    return this.getAll().some((i) => String(i.id) === String(id));
  },

  _dispatch() {
    document.dispatchEvent(new CustomEvent('compare:updated'));
  },
};

// Expose globally so custom-product-card.js can access it.
window.CompareStore = CompareStore;

// ---------------------------------------------------------------------------
// CompareBarElement
// ---------------------------------------------------------------------------

class CompareBarElement extends HTMLElement {
  connectedCallback() {
    this._onUpdate = () => this._render();
    document.addEventListener('compare:updated', this._onUpdate);
    this._render();
  }

  disconnectedCallback() {
    document.removeEventListener('compare:updated', this._onUpdate);
  }

  _render() {
    const products = CompareStore.getAll();
    const count = products.length;

    if (count === 0) {
      this.setAttribute('hidden', '');
      return;
    }

    this.removeAttribute('hidden');

    const t = window.compareTranslations || {};

    const countText = this.querySelector('[data-compare-count]');
    if (countText) {
      if (count === 1) {
        countText.textContent = t.count_one || '1 product selected';
      } else {
        countText.textContent = (t.count_other || '{{ count }} products selected').replace(
          '{{ count }}',
          String(count)
        );
      }
    }

    const mobileBtnLabel = this.querySelector('[data-compare-count-mobile]');
    if (mobileBtnLabel) {
      mobileBtnLabel.textContent = (t.compare_count || 'Compare {{ count }}').replace(
        '{{ count }}',
        String(count)
      );
    }
  }
}

// ---------------------------------------------------------------------------
// CompareModalElement
// ---------------------------------------------------------------------------

class CompareModalElement extends HTMLElement {
  connectedCallback() {
    this._onUpdate = () => {
      if (!this.hasAttribute('hidden')) this._buildTable();
    };
    document.addEventListener('compare:updated', this._onUpdate);

    // Close on backdrop click
    this.addEventListener('click', (e) => {
      if (e.target === this.querySelector('[data-compare-backdrop]')) this.close();
    });

    // Close on Escape
    this._onKeydown = (e) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('compare:updated', this._onUpdate);
    document.removeEventListener('keydown', this._onKeydown);
  }

  open() {
    this.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    this._buildTable();
    // Focus close button
    const closeBtn = this.querySelector('[data-compare-close]');
    if (closeBtn) setTimeout(() => closeBtn.focus(), 50);
  }

  close() {
    this.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }

  _getRowConfig() {
    // Read row config from .js-compare-row-data scripts on the page
    const scripts = document.querySelectorAll('.js-compare-row-data');
    if (!scripts.length) return [];
    const seenKeys = new Set();
    const seenLabels = new Set();
    const rows = [];
    scripts.forEach((s) => {
      try {
        const row = JSON.parse(s.textContent);
        const normalizedKey = String(row.metafield_key || '').trim().toLowerCase();
        const normalizedLabel = String(row.label || '').trim().toLowerCase();

        if (!normalizedKey && !normalizedLabel) return;

        if ((normalizedKey && seenKeys.has(normalizedKey)) || (normalizedLabel && seenLabels.has(normalizedLabel))) {
          return;
        }

        if (normalizedKey) seenKeys.add(normalizedKey);
        if (normalizedLabel) seenLabels.add(normalizedLabel);

        rows.push({
          label: row.label,
          metafield_key: row.metafield_key,
          display_type: row.display_type,
        });
      } catch (_) {}
    });
    return rows;
  }

  _getCellImage(product, metafieldKey) {
    const images = product.metafield_images || {};
    return images[metafieldKey] || '';
  }

  _getCellValue(product, metafieldKey) {
    if (!metafieldKey) return '';

    // Support virtual keys for core pricing fields (not metafields).
    if (metafieldKey === 'product.price') {
      return product.price_formatted || '';
    }
    if (metafieldKey === 'product.compare_at_price') {
      if (product.compare_at_price > product.price) {
        return product.compare_at_price_formatted || '';
      }
      return '';
    }

    const mf = product.metafields || {};
    return mf[metafieldKey] !== undefined && mf[metafieldKey] !== null
      ? String(mf[metafieldKey])
      : '';
  }

  _buildTable() {
    const products = CompareStore.getAll();
    const rowConfig = this._getRowConfig();
    const t = window.compareTranslations || {};

    // Update title
    const titleEl = this.querySelector('[data-compare-title]');
    if (titleEl) {
      if (products.length === 1) {
        titleEl.textContent = t.modal_title_one || 'Compare 1 product';
      } else {
        titleEl.textContent = (
          t.modal_title_other || 'Compare {{ count }} products'
        ).replace('{{ count }}', String(products.length));
      }
    }

    const wrap = this.querySelector('[data-compare-table-wrap]');
    if (!wrap) return;

    if (products.length === 0) {
      wrap.innerHTML = '';
      this.close();
      return;
    }

    // ----- Desktop table -----
    // Structure:
    //  <table>
    //    <thead> product images/names + buy buttons (one col per product)
    //    <tbody> attribute rows — first cell is sticky label, remaining are product values

    const table = document.createElement('table');
    table.className = 'compare-table';

    // --- thead ---
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');

    // Empty top-left cell (over label column)
    const cornerTh = document.createElement('th');
    cornerTh.className = 'compare-table__corner compare-table__label-cell';
    cornerTh.textContent = t.product || 'Product';
    headerRow.appendChild(cornerTh);

    products.forEach((product) => {
      const th = document.createElement('th');
      th.className = 'compare-table__product-header';

      const imgSrc = product.image || '';
      const imgEl = imgSrc
        ? `<img class="compare-table__product-image" src="${imgSrc}" alt="${escHtml(product.title)}" loading="lazy" />`
        : `<div class="compare-table__product-image compare-table__product-image--placeholder"></div>`;

      const badge = product.badge
        ? `<span class="compare-table__badge">${escHtml(product.badge)}</span>`
        : '';

      let priceHtml = '';
      if (product.price_varies) {
        priceHtml = `<span class="compare-table__price-prefix">${escHtml(product.price_prefix || 'From')}</span> <span class="compare-table__price">${escHtml(product.price_formatted || '')}</span>`;
      } else {
        const compareAt =
          product.compare_at_price > product.price
            ? `<s class="compare-table__compare-at">${escHtml(product.compare_at_price_formatted || '')}</s>`
            : '';
        priceHtml = `${compareAt}<span class="compare-table__price">${escHtml(product.price_formatted || '')}</span>`;
      }

      th.innerHTML = `
        <div class="compare-table__product-inner">
          <div class="compare-table__product-media">
            ${imgEl}
            ${badge}
          </div>
          <div class="compare-table__product-title" >${escHtml(product.title || '')}</div>
          <div class="compare-table__product-actions compare-table__product-actions--mobile">
            ${this._buildBuyPanel(product, 'mobile')}
            
          </div>
        </div>
      `;
      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    table.appendChild(thead);

    // --- tbody (attribute rows) ---
    const tbody = document.createElement('tbody');

    rowConfig.forEach((row) => {
      const tr = document.createElement('tr');
      tr.className = 'compare-table__row';

      // Sticky label cell
      const labelTd = document.createElement('td');
      labelTd.className = 'compare-table__label-cell';
      labelTd.textContent = row.label || '';
      tr.appendChild(labelTd);

      products.forEach((product) => {
        const td = document.createElement('td');
        td.className = 'compare-table__value-cell';

        // Mobile: prepend inline label
        const inlineLabel = document.createElement('span');
        inlineLabel.className = 'compare-table__inline-label';
        inlineLabel.textContent = row.label || '';
        td.appendChild(inlineLabel);

        const val = this._getCellValue(product, row.metafield_key);

        switch (row.display_type) {
          case 'bar': {
            const pct = val ? Math.min(100, parseFloat(val) || 0) : 0;
            const barWrap = document.createElement('div');
            barWrap.className = 'compare-table__bar-wrap';
            barWrap.innerHTML = `
              <span class="compare-table__bar-value">${escHtml(val)}</span>
              <span class="compare-table__bar-track"><span class="compare-table__bar-fill" style="width:${pct}%"></span></span>
            `;
            td.appendChild(barWrap);
            break;
          }
          case 'chip': {
            if (val) {
              const chip = document.createElement('span');
              chip.className = 'compare-table__chip custom-product-card__chip';
              chip.textContent = val;
              td.appendChild(chip);
            } else {
              td.appendChild(document.createTextNode('—'));
            }
            break;
          }
          case 'image': {
            if (val) {
              const chip = document.createElement('div');
              chip.className = 'compare-table__chip custom-product-card__chip';
              const imgSrc = this._getCellImage(product, row.metafield_key);
              if (imgSrc) {
                const img = document.createElement('img');
                img.src = imgSrc;
                img.alt = val;
                img.width = 'auto';
                img.height = 'auto';
                chip.appendChild(img);
              }
              chip.appendChild(document.createTextNode(val));
              td.appendChild(chip);
            } else {
              td.appendChild(document.createTextNode('—'));
            }
            break;
          }
          case 'price': {
            // treat value as cents if numeric, else display raw
            const span = document.createElement('span');
            span.className = 'compare-table__price';
            span.textContent = val || '—';
            td.appendChild(span);
            break;
          }
          default: {
            const span = document.createElement('span');
            span.textContent = val || '';
            td.appendChild(span);
          }
        }

        tr.appendChild(td);
      });

      tbody.appendChild(tr);
    });

    table.appendChild(tbody);

    // --- tfoot (desktop buy-button row) ---
    const tfoot = document.createElement('tfoot');
    const actionsRow = document.createElement('tr');
    actionsRow.className = 'compare-table__actions-row';

    // Empty corner cell to align with sticky label column
    const cornerFoot = document.createElement('td');
    cornerFoot.className = 'compare-table__corner';
    actionsRow.appendChild(cornerFoot);

    products.forEach((product) => {
      const td = document.createElement('td');
      td.className = 'compare-table__value-cell compare-table__value-cell--actions';
      td.innerHTML = `<div class="compare-table__product-actions compare-table__product-actions--desktop">${this._buildBuyPanel(product, 'desktop')}</div>`;
      actionsRow.appendChild(td);
    });

    tfoot.appendChild(actionsRow);
    table.appendChild(tfoot);

    wrap.innerHTML = '';
    wrap.appendChild(table);
  }

  _buildBuyPanel(product, suffix = '') {
    const t = window.compareTranslations || {};
    const cardId = suffix ? `compare-buy-${suffix}-${product.id}` : `compare-buy-${product.id}`;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const firstVariant = variants[0] || {};

    const variantOptions =
      variants.length > 1
        ? variants
            .map(
              (v, i) => `
          <label class="compare-buy__format${i === 0 ? ' is-selected' : ''}${!v.available ? ' is-unavailable' : ''}">
            <input type="radio" name="variant-${cardId}" value="${v.id}"
              data-price="${v.price}"
              data-price-formatted="${escHtml(v.price_formatted || '')}"
              data-available="${v.available}"
              ${i === 0 ? 'checked' : ''} />
            <span class="compare-buy__format-title">${escHtml(v.title || '')}</span>
            <span class="compare-buy__format-price">${escHtml(v.price_formatted || '')}</span>
          </label>`
            )
            .join('')
        : '';

    const formatsHtml = variantOptions
      ? `<div class="compare-buy__formats-title">${t.available_formats || 'Available formats'}</div>
         <div class="compare-buy__formats-list" role="radiogroup">${variantOptions}</div>`
      : '';

    return `
      <div class="compare-buy" id="${cardId}" data-product-id="${product.id}">
        <button type="button" class="compare-buy__toggle" data-compare-buy-toggle>
          <span>${t.buy || 'Buy'}</span>
          <svg viewBox="0 0 12 8" width="12" height="8" aria-hidden="true">
            <path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="compare-buy__panel" hidden data-compare-buy-panel>
          ${formatsHtml}
          <div class="compare-buy__purchase">
            <span class="compare-buy__qty-label">${t.qty || 'Qt:'}</span>
            <div class="compare-buy__qty">
              <button type="button" class="compare-buy__qty-btn" data-qty-dec aria-label="Decrease">−</button>
              <input type="number" class="compare-buy__qty-input" value="1" min="1" data-qty-input />
              <button type="button" class="compare-buy__qty-btn" data-qty-inc aria-label="Increase">+</button>
            </div>
            <span class="compare-buy__line-total" data-line-total>${escHtml(firstVariant.price_formatted || '')}</span>
            <button type="button" class="compare-buy__add-btn" data-add-to-cart>
              <span class="compare-buy__add-text">${t.buy || 'Buy'}</span>
              <span class="compare-buy__add-added" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
            </button>
          </div>
          <button type="button" class="compare-buy__close-btn" data-compare-buy-close>
            ${t.close_buy || 'Close'}
          </button>
        </div>
        <div class="compare-buy__extra-actions">
          <a href="#" class="compare-buy__favorite button button--secondary h-6" tabindex="0" role="button">
            <img src="${t.icon_favorite_url || ''}" class="compare-buy__favorite-icon" alt="" width="20" height="20" aria-hidden="true" />
            <span class="compare-buy__favorite-label">${t.add_to_favorites || 'Add to favorites'}</span>
          </a>
          <a href="${escHtml(product.url || '#')}" class="compare-buy__learn-more" target="_blank" rel="noopener noreferrer">${t.learn_more || 'Learn more'}</a>
        </div>
      </div>`;
  }
}

// ---------------------------------------------------------------------------
// Delegated event handling for buy panels inside the modal
// ---------------------------------------------------------------------------

function handleCompareBuyEvents(modal) {
  modal.addEventListener('click', (e) => {
    const el = /** @type {HTMLElement} */ (e.target);

    // Toggle buy panel
    const toggleBtn = el.closest('[data-compare-buy-toggle]');
    if (toggleBtn) {
      const buyEl = toggleBtn.closest('.compare-buy');
      const panel = buyEl?.querySelector('[data-compare-buy-panel]');
      if (panel) {
        const isOpen = !panel.hasAttribute('hidden');
        panel.toggleAttribute('hidden', isOpen);
        toggleBtn.closest('.compare-buy')?.classList.toggle('is-open', !isOpen);
      }
      return;
    }

    // Close buy panel
    const closeBtn = el.closest('[data-compare-buy-close]');
    if (closeBtn) {
      const buyEl = closeBtn.closest('.compare-buy');
      buyEl?.querySelector('[data-compare-buy-panel]')?.setAttribute('hidden', '');
      buyEl?.classList.remove('is-open');
      return;
    }

    // Qty +/-
    if (el.closest('[data-qty-dec]') || el.closest('[data-qty-inc]')) {
      const buyEl = el.closest('.compare-buy');
      const input = buyEl?.querySelector('[data-qty-input]');
      if (input instanceof HTMLInputElement) {
        const current = Math.max(1, parseInt(input.value, 10) || 1);
        input.value = String(el.closest('[data-qty-dec]') ? Math.max(1, current - 1) : current + 1);
        updateLineTotal(buyEl);
      }
      return;
    }

    // Add to cart
    const addBtn = el.closest('[data-add-to-cart]');
    if (addBtn) {
      handleAddToCart(addBtn.closest('.compare-buy'));
      return;
    }
  });

  // Variant change
  modal.addEventListener('change', (e) => {
    const input = /** @type {HTMLInputElement} */ (e.target);
    if (!input.name?.startsWith('variant-')) return;
    const buyEl = input.closest('.compare-buy');
    if (!buyEl) return;

    // Update is-selected
    buyEl.querySelectorAll('.compare-buy__format').forEach((label) => {
      const inp = label.querySelector('input');
      label.classList.toggle('is-selected', inp?.checked ?? false);
    });

    updateLineTotal(buyEl);
  });
}

function updateLineTotal(buyEl) {
  if (!buyEl) return;
  const checked = buyEl.querySelector('input[type="radio"]:checked');
  const qty = Math.max(1, parseInt(buyEl.querySelector('[data-qty-input]')?.value || '1', 10));
  const priceCents = parseInt(checked?.dataset.price || '0', 10);
  const total = priceCents * qty;
  const lineTotalEl = buyEl.querySelector('[data-line-total]');
  if (lineTotalEl) {
    const sample = checked?.dataset.priceFormatted;
    const amount = (total / 100).toFixed(2);
    if (sample) {
      const replaced = sample.replace(/[\d,.]+/, amount);
      lineTotalEl.textContent = replaced !== sample ? replaced : `$${amount}`;
    } else {
      lineTotalEl.textContent = `$${amount}`;
    }
  }
}

function handleAddToCart(buyEl) {
  if (!buyEl) return;
  const productId = buyEl.dataset.productId;
  const variantInput = buyEl.querySelector('input[type="radio"]:checked');
  // If only one variant and no radio buttons, use the first hidden-ish input value
  const variantId =
    variantInput?.value ||
    buyEl.querySelector('input[type="radio"]')?.value ||
    null;

  const qtyInput = buyEl.querySelector('[data-qty-input]');
  const quantity = Math.max(1, parseInt(qtyInput?.value || '1', 10));
  const addBtn = buyEl.querySelector('[data-add-to-cart]');

  if (!variantId) return;

  const originalText = addBtn?.textContent ?? '';
  if (addBtn) {
    addBtn.disabled = true;
    addBtn.setAttribute('aria-busy', 'true');
  }

  const cartItemSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

  const body = new FormData();
  body.append('id', variantId);
  body.append('quantity', String(quantity));
  if (cartItemSectionIds.length) body.append('sections', cartItemSectionIds.join(','));

  fetch(`${window.Theme?.routes?.cart_add_url ?? '/cart/add'}.js`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body,
  })
    .then((r) => {
      if (!r.ok) return r.json().then((err) => Promise.reject(err))
      return r.json()
    })
    .then((response) => {
      if (response?.status && response.status !== 200) {
        if (addBtn) addBtn.textContent = response.message || 'Error';
        setTimeout(() => {
          if (addBtn) addBtn.textContent = originalText;
        }, 2500);
        return;
      }

      const cartAddEvent = new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: response, sourceId: productId, data: { productId, variantId, itemCount: quantity, sections: response?.sections } },
      });
      document.dispatchEvent(cartAddEvent);

      if (qtyInput instanceof HTMLInputElement) qtyInput.value = '1';
      updateLineTotal(buyEl);

      if (addBtn) {
        addBtn.dataset.added = 'true';
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-busy');
      }
    })
    .catch((err) => {
      console.error('[compare-modal] add to cart blocked/failed', err)
      // Show error on button — toast already shown by gate
      if (addBtn) {
        addBtn.textContent = err?.error || 'Not allowed'
        setTimeout(() => {
          if (addBtn) addBtn.textContent = originalText
        }, 2500)
      }
    })
    .finally(() => {
      if (addBtn?.dataset.added === 'true') {
        setTimeout(() => {
          delete addBtn.dataset.added;
          addBtn.disabled = false;
          addBtn.removeAttribute('aria-busy');
        }, 1500);
      } else {
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.removeAttribute('aria-busy');
        }
      }
    });
}
// ---------------------------------------------------------------------------
// Register custom elements
// ---------------------------------------------------------------------------

if (!customElements.get('compare-bar')) {
  customElements.define('compare-bar', CompareBarElement);
}

if (!customElements.get('compare-modal')) {
  customElements.define('compare-modal', CompareModalElement);
}

// ---------------------------------------------------------------------------
// Boot: wire up bar buttons and modal events on DOMContentLoaded
// ---------------------------------------------------------------------------

function boot() {
  const bar = document.querySelector('compare-bar');
  const modal = /** @type {CompareModalElement|null} */ (document.querySelector('compare-modal'));

  if (bar) {
    bar.querySelector('[data-compare-now]')?.addEventListener('click', () => {
      modal?.open();
    });

    bar.querySelector('[data-compare-clear]')?.addEventListener('click', () => {
      modal?.close();
      CompareStore.clear();
    });
  }

  if (modal) {
    modal.querySelector('[data-compare-close]')?.addEventListener('click', () => modal.close());
    handleCompareBuyEvents(modal);
  }

  // Defer so all custom-product-card connectedCallbacks have run first
  // and their 'compare:updated' listeners are attached.
  setTimeout(() => {
    // Sync bar (always needed — even if count is 0 to ensure bar is hidden).
    document.dispatchEvent(new CustomEvent('compare:updated'));
    // Directly sync every card checkbox already in the DOM.
    document.querySelectorAll('custom-product-card').forEach((card) => {
      if (typeof card.syncCompareState === 'function') card.syncCompareState();
    });
  }, 0);

  // Watch for new product cards added by infinite scroll
  const grid = document.querySelector('.product-grid-container');
  if (grid && typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          node.querySelectorAll('custom-product-card').forEach((card) => {
            if (card instanceof HTMLElement && typeof card.syncCompareState === 'function') {
              card.syncCompareState();
            }
          });
        });
      });
    });
    observer.observe(grid, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function escHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
